/**
 * The campaign record's query hooks: read one campaign, edit it, and move it between states.
 *
 * The state change is its own mutation because it has consequences the edit does not: archiving
 * releases a slot (§5.1.1), so it also refreshes the tenant's campaign list and capacity, and
 * the session's `me` (the switcher lists campaigns).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiFailureError } from '../../../../lib/http/errors';
import { useApiClient } from '../../../session/client';
import { ME_QUERY_KEY } from '../../../session/session';
import { CAPACITY_QUERY_KEY } from '../../../tenant/capacity/api/queries';
import { CAMPAIGNS_QUERY_KEY } from '../../../tenant/home/useCampaigns';
import {
  allianceWrite,
  alliancesSchema,
  campaignDetailSchema,
  presenceSchema,
  type Alliance,
  type CampaignDetail,
  type CampaignState,
  type CampaignUpdate,
  type Presence,
  type PresenceWrite,
} from './schemas';
import { GET_ROUTES, routePath } from '../../../../lib/http/routes';

export const CAMPAIGN_RECORD_QUERY_KEY = (campaignId: string) =>
  ['campaign', campaignId, 'record'] as const;

export function useCampaignRecord(
  campaignId: string,
): UseQueryResult<CampaignDetail, ApiFailureError> {
  const client = useApiClient();
  return useQuery<CampaignDetail, ApiFailureError>({
    queryKey: CAMPAIGN_RECORD_QUERY_KEY(campaignId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.campaignDetail, { campaignId }),
        GET_ROUTES.campaignDetail.schema,
      ),
    enabled: campaignId !== '',
    // Every write to this record (`useUpdateCampaign`, `useSetCampaignState`,
    // `useAddPresence`, `useRemovePresence` below) patches this same cache entry directly
    // rather than invalidating it, so nothing here relies on a background refetch for
    // freshness -- default `staleTime` would instead cost a second GET every time a second
    // observer (`./Presence`, mounted alongside `CampaignRecord`'s own read) subscribes to
    // this key. `query.refetch()` (`ErrorState`'s retry) still forces one regardless.
    staleTime: Infinity,
  });
}

export function useUpdateCampaign(
  campaignId: string,
): UseMutationResult<CampaignDetail, ApiFailureError, CampaignUpdate> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CampaignDetail, ApiFailureError, CampaignUpdate>({
    mutationFn: (body) => client.putParsed(`/campaigns/${campaignId}`, body, campaignDetailSchema),
    onSuccess: async (campaign) => {
      queryClient.setQueryData(CAMPAIGN_RECORD_QUERY_KEY(campaignId), campaign);
      await queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY(campaign.tenant_id) });
    },
  });
}

/** `POST /campaigns/{id}/presences`: appends the added row to the cached record rather than
 *  refetching it whole, same reasoning as `useUpdateCampaign`'s `setQueryData` below. */
export function useAddPresence(
  campaignId: string,
): UseMutationResult<Presence, ApiFailureError, PresenceWrite> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Presence, ApiFailureError, PresenceWrite>({
    mutationFn: (body) =>
      client.postParsed(`/campaigns/${campaignId}/presences`, body, presenceSchema),
    onSuccess: (presence) => {
      queryClient.setQueryData<CampaignDetail>(CAMPAIGN_RECORD_QUERY_KEY(campaignId), (prev) =>
        prev === undefined ? prev : { ...prev, presences: [...prev.presences, presence] },
      );
    },
  });
}

/** `DELETE /campaigns/{id}/presences/{presenceId}`: mutated with the presence's own id, which
 *  also lets a caller tell which row a pending remove targets (`mutation.variables`). */
export function useRemovePresence(
  campaignId: string,
): UseMutationResult<void, ApiFailureError, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiFailureError, string>({
    mutationFn: (presenceId) => client.del(`/campaigns/${campaignId}/presences/${presenceId}`),
    onSuccess: (_data, presenceId) => {
      queryClient.setQueryData<CampaignDetail>(CAMPAIGN_RECORD_QUERY_KEY(campaignId), (prev) =>
        prev === undefined
          ? prev
          : { ...prev, presences: prev.presences.filter((row) => row.id !== presenceId) },
      );
    },
  });
}

export function useSetCampaignState(
  campaignId: string,
): UseMutationResult<CampaignDetail, ApiFailureError, CampaignState> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CampaignDetail, ApiFailureError, CampaignState>({
    mutationFn: (state) =>
      client.postParsed(`/campaigns/${campaignId}/state`, { state }, campaignDetailSchema),
    onSuccess: async (campaign) => {
      queryClient.setQueryData(CAMPAIGN_RECORD_QUERY_KEY(campaignId), campaign);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY(campaign.tenant_id) }),
        queryClient.invalidateQueries({ queryKey: CAPACITY_QUERY_KEY(campaign.tenant_id) }),
        queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
      ]);
    },
  });
}

export const ALLIANCES_QUERY_KEY = (campaignId: string) =>
  ['campaign', campaignId, 'alliances'] as const;

export function useAlliances(campaignId: string): UseQueryResult<Alliance[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<Alliance[], ApiFailureError>({
    queryKey: ALLIANCES_QUERY_KEY(campaignId),
    queryFn: async () =>
      (
        await client.getParsed(
          routePath(GET_ROUTES.campaignAlliances, { campaignId }),
          GET_ROUTES.campaignAlliances.schema,
        )
      ).items,
    enabled: campaignId !== '',
  });
}

/** What the TSE record suggests; nothing is stored until the list is saved. */
export function useAllianceSuggestions(
  campaignId: string,
  enabled: boolean,
): UseQueryResult<Alliance[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<Alliance[], ApiFailureError>({
    queryKey: [...ALLIANCES_QUERY_KEY(campaignId), 'suggestions'],
    queryFn: async () =>
      (
        await client.getParsed(
          routePath(GET_ROUTES.allianceSuggestions, { campaignId }),
          GET_ROUTES.allianceSuggestions.schema,
        )
      ).items,
    enabled: enabled && campaignId !== '',
  });
}

/** TSE candidatures, parties and global entities matching `q`; idle below two characters. */
export function useAllianceOptions(
  campaignId: string,
  q: string,
): UseQueryResult<Alliance[], ApiFailureError> {
  const client = useApiClient();
  const query = q.trim();
  return useQuery<Alliance[], ApiFailureError>({
    queryKey: [...ALLIANCES_QUERY_KEY(campaignId), 'options', query],
    queryFn: async () =>
      (
        await client.getParsed(
          routePath(GET_ROUTES.allianceOptions, { campaignId }, { q: query }),
          GET_ROUTES.allianceOptions.schema,
        )
      ).items,
    enabled: campaignId !== '' && query.length >= 2,
    placeholderData: (previous) => previous,
  });
}

export function useSaveAlliances(
  campaignId: string,
): UseMutationResult<Alliance[], ApiFailureError, Alliance[]> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Alliance[], ApiFailureError, Alliance[]>({
    mutationFn: async (items) =>
      (
        await client.putParsed(
          `/campaigns/${campaignId}/alliances`,
          { items: items.map(allianceWrite) },
          alliancesSchema,
        )
      ).items,
    onSuccess: (items) => {
      queryClient.setQueryData(ALLIANCES_QUERY_KEY(campaignId), items);
    },
  });
}
